import React, { useState, useEffect } from 'react';
import { partialCloseTradeApi } from '../api/api';

const PartialCloseModal = ({ isOpen, onClose, trade, onSuccessfulPartialClose }) => {
  const [volumeToClose, setVolumeToClose] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setVolumeToClose('');
      setError(null);
      setSuccessMessage('');
    }
  }, [isOpen]);

  if (!isOpen || !trade) {
    return null;
  }

  const remainingSize = parseFloat(trade.lot_size); // Assuming lot_size is current remaining size. Adjust if a different field is used.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage('');

    const volume = parseFloat(volumeToClose);
    if (isNaN(volume) || volume <= 0) {
      setError({ detail: { volume_to_close: ['Volume must be a positive number.'] } });
      setIsLoading(false);
      return;
    }
    if (volume >= remainingSize) {
      setError({ detail: { volume_to_close: [`Volume to close (${volume.toFixed(2)}) must be less than remaining size (${remainingSize.toFixed(2)}). For full closure, use the main close button.`] } });
      setIsLoading(false);
      return;
    }

    try {
      const response = await partialCloseTradeApi(trade.trade_id, String(volume));
      setSuccessMessage(response.message || 'Trade partially closed successfully!');
      // Optionally display more info from response: response.closed_volume, response.remaining_volume, response.profit_on_closed_portion
      if (onSuccessfulPartialClose) {
        onSuccessfulPartialClose();
      }
    } catch (apiError) {
      console.error("Partial Close Error from API:", apiError);
      if (apiError && (apiError.detail || apiError.error)) {
        setError(apiError);
      } else if (typeof apiError === 'string') {
        setError({ detail: apiError });
      } else {
        setError({ detail: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldErrorMessage = (field) => {
    if (error && typeof error.detail === 'object' && error.detail[field] && Array.isArray(error.detail[field])) {
      return error.detail[field].join(' ');
    }
    return null;
  };

  const getGeneralErrorMessage = () => {
    if (error) {
      if (typeof error.detail === 'string') return error.detail;
      if (Array.isArray(error.detail) && error.detail.every(item => typeof item === 'string')) return error.detail.join(' ');
      if (typeof error.error === 'string') return error.error;
    }
    return null;
  };

  const generalError = getGeneralErrorMessage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-backgroundSurface p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-textPrimary">Partially Close Trade {trade.position_id}</h2>
          <button onClick={onClose} className="text-textSecondary hover:text-textPrimary">&times;</button>
        </div>

        {successMessage && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{successMessage}</div>}
        {generalError && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{generalError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <p className="text-sm text-textSecondary">Current Remaining Lot Size: {remainingSize.toFixed(2)}</p>
          </div>
          <div className="mb-4">
            <label htmlFor="volumeToClose" className="block text-sm font-medium text-textSecondary mb-1">Volume to Close</label>
            <input
              type="number"
              step="0.01" // Standard lot step
              id="volumeToClose"
              value={volumeToClose}
              onChange={(e) => setVolumeToClose(e.target.value)}
              className="w-full p-2 border border-borderDefault rounded-md bg-backgroundInput text-textPrimary"
              placeholder="e.g., 0.05"
              required
            />
            {getFieldErrorMessage('volume_to_close') && <p className="text-xs text-destructive mt-1">{getFieldErrorMessage('volume_to_close')}</p>}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="btn btn-secondary px-4 py-2 rounded-md text-textPrimary hover:bg-secondary/80 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary px-4 py-2 rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Closing...' : 'Partially Close'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartialCloseModal;
